import React, { useEffect, useRef, useState } from 'react'
import { CloudinaryVideo, Icons, PreloadOptions } from 'msp-components'
import { useWindowSize } from 'msp-integrations'
import { Equalizer } from '@/components/atoms/Equalizer'
import VideoProgressBar from './partials/VideoProgressBar'
import { VideoStandaloneProps } from './VideoStandalone.types'

const VideoStandalone = ({
  className,
  cloudName,
  publicId,
  publicIdMobile,
  hideFullWidthButton,
  hideMuteButton,
  hidePlayPauseButton,
  hideProgressBar,
  playButtonPosition = 'right',
  disableProgressBarClick,
  autoPlay = true,
  loop = true,
  muted = true,
  showPuroControls,
  children,
  title
}: VideoStandaloneProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(muted)
  const [videoProgress, setVideoProgress] = useState(0)
  const { width, breakpoints } = useWindowSize()
  const isMobile = width && width < breakpoints.md

  const togglePlay = () => {
    setIsPlaying(!isPlaying)
  }

  const toggleMute = () => {
    const newMuted = !isMuted
    if (videoRef.current) {
      videoRef.current.muted = newMuted
      videoRef.current.volume = newMuted ? 0 : 0.8
    }
    setIsMuted(newMuted)
  }

  const scrollYBeforeFullscreenRef = useRef<number | null>(null)

  const toggleFullScreen = () => {
    if (videoRef.current) {
      const video = videoRef.current as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void
        requestFullscreen?: () => Promise<void>
      }
      scrollYBeforeFullscreenRef.current = window.scrollY
      if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen()
      } else if (typeof video.requestFullscreen === 'function') {
        video.requestFullscreen()
      }
    }
  }

  const handleTimeUpdate = () => {
    if (videoRef?.current) {
      const progressBarPercentage = Math.ceil(
        (videoRef.current.currentTime / videoRef.current.duration) * 100
      )
      setVideoProgress(progressBarPercentage)
    }
  }

  const handleVideoSkip = (e: MouseEvent) => {
    if (!progressBarRef.current || !videoRef.current) return
    const skipMoment =
      (e.offsetX / progressBarRef.current.offsetWidth) *
      videoRef.current.duration
    videoRef.current.currentTime = skipMoment
  }

  useEffect(() => {
    if (autoPlay) {
      togglePlay()
    }
  }, [autoPlay])

  useEffect(() => {
    const timeUpdateHandler = handleTimeUpdate
    const mouseUpHandler = (e: MouseEvent) => handleVideoSkip(e)

    if (showPuroControls && !hideProgressBar && videoRef.current) {
      videoRef.current.addEventListener('timeupdate', timeUpdateHandler)
    }

    if (
      showPuroControls &&
      progressBarRef.current &&
      !disableProgressBarClick &&
      !hideProgressBar
    ) {
      progressBarRef.current.addEventListener('mouseup', mouseUpHandler)
    }

    return () => {
      videoRef?.current?.removeEventListener('timeupdate', timeUpdateHandler)
      progressBarRef?.current?.removeEventListener('mouseup', mouseUpHandler)
    }
  }, [showPuroControls])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    const restoreScroll = () => {
      const y = scrollYBeforeFullscreenRef.current
      if (y === null) return
      scrollYBeforeFullscreenRef.current = null
      requestAnimationFrame(() => window.scrollTo(0, y))
    }
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) restoreScroll()
    }
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('webkitendfullscreen', restoreScroll)
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => {
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('webkitendfullscreen', restoreScroll)
      document.removeEventListener('fullscreenchange', onFullscreenChange)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (isPlaying && video.paused) {
      video
        .play()
        .then((_) => {})
        .catch((error) => {})
    } else if (!isPlaying && !video.paused) {
      video.pause()
    }
  }, [isPlaying])

  useEffect(() => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = 0
        videoRef.current.muted = true
      } else {
        videoRef.current.volume = 0.7
        videoRef.current.muted = false
      }
    }
  }, [isMuted])

  return (
    <div
      className={`relative h-full w-full transition-all duration-500 ease-in ${className}`}
    >
      <CloudinaryVideo
        ref={videoRef}
        className={'object-cover'}
        cloudName={cloudName}
        publicId={isMobile && publicIdMobile ? publicIdMobile : publicId}
        autoplay={autoPlay}
        controls={!showPuroControls}
        loop={loop}
        muted={isMuted}
        preload={PreloadOptions.none}
        title={title}
      />
      <div
        className={`absolute flex flex-col justify-items-center fill-white text-white ${
          showPuroControls ? 'inset-0' : 'left-0 top-0 h-4/5 w-full'
        }`}
      >
        <div
          className={
            'text-44 tracking-0 leading-56 md:text-60 md:leading-72 md:tracking-0 flex h-full w-full select-none flex-col items-center justify-around self-center overflow-hidden text-center md:w-[40vw]'
          }
        >
          {children}
        </div>
        {showPuroControls && (
          <div
            className={
              'flex h-auto w-full items-center space-x-4 bg-black/50 stroke-white stroke-2 px-8 py-6 backdrop-blur-xl md:space-x-6'
            }
          >
            {!hidePlayPauseButton && playButtonPosition === 'left' && (
              <button
                aria-label={isPlaying ? 'Pause' : 'Play'}
                className={
                  'cc-outline-no-offset-white flex-none cursor-pointer'
                }
                onClick={() => togglePlay()}
              >
                <Icons
                  height={'25px'}
                  iconName={isPlaying ? 'Pause' : 'Play'}
                  width={'25px'}
                />
              </button>
            )}
            {!hideMuteButton && (
              <button
                onClick={() => toggleMute()}
                className={
                  'cc-outline-no-offset-white flex-none cursor-pointer'
                }
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {!isMuted ? (
                  <Equalizer className={'h-6 w-7'} paused={!isPlaying} />
                ) : (
                  <Icons height={'25px'} iconName={'SoundMute'} />
                )}
              </button>
            )}
            <div className={'flex flex-auto justify-center'}>
              {!hideProgressBar && (
                <VideoProgressBar
                  ref={progressBarRef}
                  percentage={videoProgress}
                  clickable={!disableProgressBarClick}
                />
              )}
            </div>
            {!hidePlayPauseButton && playButtonPosition === 'right' && (
              <button
                aria-label={isPlaying ? 'Pause' : 'Play'}
                className={
                  'cc-outline-no-offset-white flex-none cursor-pointer'
                }
                onClick={() => togglePlay()}
              >
                <Icons
                  height={'25px'}
                  iconName={isPlaying ? 'Pause' : 'Play'}
                  width={'25px'}
                />
              </button>
            )}
            {!hideFullWidthButton && (
              <button
                aria-label={'Fullscreen'}
                className={
                  'cc-outline-no-offset-white flex-none cursor-pointer'
                }
                onClick={() => toggleFullScreen()}
              >
                <Icons
                  height={'25px'}
                  iconName={'VideoExpand'}
                  width={'25px'}
                />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default VideoStandalone
